import { computed } from 'vue'

import { useAuthStore } from '../stores/authStore.js'
import { useScopeStore } from '../stores/scopeStore.js'

import scopeUtility from '../utils/scope.utility.js'

import { ROUTE_PATHS } from '../config/route.config.js'
import { SCOPES } from '../config/scope.config.js'

function isTicketPath(path) {
  return typeof path === 'string' && path.includes('/tehtavat/')
}

function isAdminPath(path) {
  return path === ROUTE_PATHS.admin || path.startsWith(`${ROUTE_PATHS.admin}/`)
}

export function useScope() {
  const authStore = useAuthStore()
  const scopeStore = useScopeStore()

  const resolved = computed(() => scopeUtility.ids(scopeStore.scope))

  /**
   * Set account scope — the current account as both universe and active set.
   * Returns `false` when there is no user or the scope is unchanged.
   *
   * @returns {boolean}
   */
  function setAccount() {
    const user = authStore.user
    if (!user) return false
    const accounts = scopeUtility.get([user.currentAccount._id])
    const candidate = { kind: SCOPES.ACCOUNT, accounts: accounts, active: accounts }
    if (scopeUtility.same(scopeStore.scope, candidate)) return false
    scopeStore.hydrate.scope(candidate)
    return true
  }

  /**
   * Set group scope — member accounts as the universe, shown in full. Loads the
   * accounts from the network when absent from the session. Returns `false`
   * when there is no user or the scope is unchanged.
   *
   * @returns {Promise<boolean>}
   */
  async function setGroup() {
    const user = authStore.user
    if (!user) return false
    let accounts = scopeUtility.get(user.accounts)
    if (!accounts.length) {
      accounts = scopeUtility.get(
        (user.ticketScopeAccounts ?? []).map((account) => account._id).filter(Boolean),
      )
    }
    if (!accounts.length) accounts = scopeUtility.get(user.allowedTicketAccountIds)
    const candidate = { kind: SCOPES.GROUP, accounts: accounts, active: accounts }
    if (scopeUtility.same(scopeStore.scope, candidate)) return false
    scopeStore.hydrate.scope(candidate)
    return true
  }

  /**
   * Set tickets scope — the full ticket universe (member ∪ ticketGroup
   * accounts), shown in full. Returns `false` when there is no user or the
   * scope is unchanged.
   *
   * @returns {boolean}
   */
  function setTickets() {
    const user = authStore.user
    if (!user) return false
    const accounts = scopeUtility.get(user.allowedTicketAccountIds)
    const candidate = { kind: SCOPES.TICKETS, accounts: accounts, active: accounts }
    if (scopeUtility.same(scopeStore.scope, candidate)) return false
    scopeStore.hydrate.scope(candidate)
    return true
  }

  /**
   * Set admin scope — all accounts, shown in full. Restricted to superAdmins.
   * Returns `false` for other roles or when the scope is unchanged.
   *
   * @returns {boolean}
   */
  function setAdmin() {
    const user = authStore.user
    if (user?.role !== 'superAdmin') return false
    const accounts = scopeUtility.get(user.accounts)
    const candidate = { kind: SCOPES.ADMIN, accounts: accounts, active: accounts }
    if (scopeUtility.same(scopeStore.scope, candidate)) return false
    scopeStore.hydrate.scope(candidate)
    return true
  }

  /**
   * Narrow `active` to a subset of the current universe. Pass `[]` to match no
   * accounts; pass the full `accounts` list (see `showAllAccounts`) for kaikki.
   *
   * @param {string[]} accountIds
   * @returns {boolean}
   */
  function filterAccounts(accountIds) {
    const { kind, accounts } = scopeStore.scope
    const active = accountIds.length ? scopeUtility.clamp(accountIds, accounts) : []
    const candidate = { kind: kind, accounts: accounts, active: active }
    if (scopeUtility.same(scopeStore.scope, candidate)) return false
    scopeStore.hydrate.scope(candidate)
    return true
  }

  /**
   * Show the full account universe — `active` = `accounts`.
   *
   * @returns {boolean}
   */
  function showAllAccounts() {
    return filterAccounts(scopeStore.scope.accounts)
  }

  /**
   * Clear scope to the logged-out defaults.
   *
   * @returns {void}
   */
  function reset() {
    scopeStore.reset()
  }

  /**
   * Restore scope from the session + current route after a tab-focus refresh.
   * Preserves the current `active` account filter when it still fits the new
   * universe (see `docs3/scopes-and-cache.md` §1 scope transitions).
   *
   * @param {{ path?: string, matched?: Array<{ meta?: object }> }} [route]
   * @returns {Promise<boolean>} `true` when scope was replaced or narrowed.
   */
  async function rehydrateFromRoute(route = {}) {
    const user = authStore.user
    if (!user) return false

    const matched = route.matched ?? []
    if (matched.some((record) => record.meta?.scopeNeutral)) return false

    const path = route.path ?? ''
    const activeBefore = [...scopeStore.scope.active]

    let changed

    // Path check covers boot before the initial navigation has matched records.
    if (matched.some((record) => record.meta?.requiresAdmin) || isAdminPath(path)) {
      changed = setAdmin()
    } else if (user.groupMode) {
      changed = isTicketPath(path) ? setTickets() : await setGroup()
    } else {
      changed = setAccount()
    }

    if (activeBefore.length) {
      changed = filterAccounts(activeBefore) || changed
    }

    return changed
  }

  return {
    setAccount,
    setGroup,
    setTickets,
    setAdmin,
    filterAccounts,
    showAllAccounts,
    rehydrateFromRoute,
    reset,
    resolved,
  }
}
